import _ from 'underscore';
import moment from 'moment';

import { shouldShowAddClientNotification, createAddClientNotification } from '../AddClientNotification/AddClientNotificationModule';
import { shouldShowSearchBoostNotification, createSearchBoostNotification } from '../SearchBoostNotification';
import { shouldShowManageGoalNotification, createManageGoalNotification } from '../ManageGoalNotification';
import { UNIT_OF_TIME } from '../../today/SearchBoost/SearchBoostContainer';
import { shouldShowAmbassadorToolkitNotification } from '../AmbassadorToolkitNotificationModule';
import { getFirstSPApptFromNotifications } from '../FirstSPClientApptBookingNotification';
import { shouldShowMarketingHubNotification, createMarketingHubNotification } from '../MarketingHubNotification/MarketingHubNotification';
import {
  shouldShowNcdCancellationPolicyNotification,
  createNcdCancellationPolicyNotification,
} from '../NcdCancellationPolicyNotification';

/**
 * A function to target a specific notification to remove from displaying in the notification feed
 * @param {array} notifications - the list of notifications
 * @param {string} notificationType - the notification type to target
 * @param {number} apptId - the appt ID to target (optional)
 * @return {array} the list of filtered notifications
 */
export const removeNotification = (notifications, notificationType, apptId) => {
  const filteredNotifications = _.reject(
    notifications,
    notification => {
      if (apptId) {
        return (
          notification.type === notificationType
          && notification.appointment_id === apptId
        );
      }
      return notification.type === notificationType;
    },
  );
  return filteredNotifications;
};

/**
 * Adds a static notification that will stick to the top
 * Simply call notifications.unshift inside this function to add sticky notifications
 * @param {object} response - the response object from the notifications endpoint
 * @param {number} providerId
 */
export const addStaticNotifications = async (response, providerId) => {
  const {
    /** list of notifications from BE */
    results: notifications,
    /** profile creation time */
    profile_creation_time: profileCreationTime,
    /** date at which pro has earned search boost and now boosted in search */
    profile_search_boost_eligible_date: profileSearchBoostEligibleDate,
    /** start date at which pro can start earning search boost by booking 6+ appts */
    profile_search_boost_earning_start_date: profileSearchBoostEarningStartDate,
    /** end date at which pro can earn a search boost by booking 6+ appts */
    profile_search_boost_earning_end_date: profileSearchBoostEarningEndDate,
    /** datetime pro extended time to earn a search boost */
    profile_search_boost_extension_creation_time: profileSearchBoostExtensionCreationTime,
  } = response;

  let filteredNotifications = notifications;

  const showSearchBoostNotification = await shouldShowSearchBoostNotification(
    profileSearchBoostEligibleDate,
    profileSearchBoostEarningStartDate,
  );

  if (showSearchBoostNotification) {
    const searchBoostNotification = await createSearchBoostNotification(
      profileSearchBoostEligibleDate,
      profileSearchBoostEarningStartDate,
      providerId,
    );
    filteredNotifications.unshift(searchBoostNotification);
  }

  // if pro has has a search boost eligible date, it means the pro has met the requirement to
  // actually be search boosted. We will want to forever hide the search boost eligibility
  // notification to prevent confusion (e.g. pro could be search boosted again after first boost)
  if (profileSearchBoostEligibleDate) {
    filteredNotifications = removeNotification(notifications, 'search_boost_eligibility');
  }

  const deadlineToExtend = moment.utc(profileSearchBoostEarningEndDate).add(7, UNIT_OF_TIME);
  const eligibleToExtendBoostExpired = moment.utc() > deadlineToExtend;

  if (profileSearchBoostExtensionCreationTime || eligibleToExtendBoostExpired) {
    filteredNotifications = removeNotification(notifications, 'extend_search_boost_eligibility');
  }

  const showAddClientNotification = await shouldShowAddClientNotification(
    profileCreationTime,
    providerId,
  );

  if (showAddClientNotification) {
    const addClientNotification = createAddClientNotification(profileCreationTime, providerId);
    filteredNotifications.unshift(addClientNotification);
  }

  const showManageGoalNotification = await shouldShowManageGoalNotification(providerId);

  if (showManageGoalNotification) {
    const manageGoalNotification = createManageGoalNotification(providerId);
    filteredNotifications.unshift(manageGoalNotification);
  }

  const showAmbassadorToolkitNotification = await shouldShowAmbassadorToolkitNotification();

  if (showAmbassadorToolkitNotification) {
    const ambassadorToolkitNotification = {
      type: 'ambassador_toolkit_announcement',
      creation_time: '2020-12-03T23:00:00Z',
      persistent: true,
      providerId,
    };
    filteredNotifications.unshift(ambassadorToolkitNotification);
  }

  const firstSmartPricedAppt = getFirstSPApptFromNotifications(filteredNotifications);
  if (firstSmartPricedAppt) {
    filteredNotifications = removeNotification(filteredNotifications, 'client_appt_booking', firstSmartPricedAppt.appointment_id);
  }

  const showMarketingHubNotification = await shouldShowMarketingHubNotification(
    profileCreationTime,
  );
  if (showMarketingHubNotification) {
    const marketingHubNotification = createMarketingHubNotification(providerId);
    filteredNotifications.unshift(marketingHubNotification);
  }

  const showNcdPolicyNotification = await shouldShowNcdCancellationPolicyNotification();
  if (showNcdPolicyNotification) {
    const ncdPolicyNotification = createNcdCancellationPolicyNotification(providerId);
    filteredNotifications.unshift(ncdPolicyNotification);
  }

  filteredNotifications.unshift({
    type: 'new_sr_stats_notification',
    persistent: true,
    provider_id: providerId,
    creation_time: new Date(),
  });

  return filteredNotifications;
};
